/* Native Teeinblue validation/addition and Impulse cart rendering stay authoritative.
   This adapter owns presentation only: pending, confirmed, failure and modal layers. */
(() => {
  'use strict';
  if (window.FizzyCartDrawer) return;
  const lifecycle = new AbortController();
  const options = { signal: lifecycle.signal };
  let drawer, overlay, pending = false, confirmed = false, button, timer, checkTimer, errorTimer;
  let nativeSuccessHandled = false, announceAddition = false;
  const root = () => button?.closest('[data-teeinblue-pdp]');
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
  const isOpen = () => drawer?.classList.contains('drawer--is-open');
  function state(value) {
    if (!drawer) return;
    if (value !== 'loading') button?.removeAttribute('aria-busy');
    drawer.dataset.fizzyCartState = value;
    drawer.querySelector('[data-fizzy-cart-loading]').hidden = value !== 'loading';
    drawer.querySelector('[data-fizzy-cart-error]').hidden = value !== 'error';
    const confirmation = drawer.querySelector('[data-fizzy-cart-confirmation]');
    if (confirmation) confirmation.hidden = value !== 'ready' || !announceAddition || !drawer.querySelector('.cart__item');
    drawer.querySelector('[data-products]').setAttribute('aria-busy', String(value === 'loading'));
    drawer.querySelector('.cart__checkout').disabled = value !== 'ready';
  }
  function begin(event) {
    if (!drawer || pending) return;
    button = document.querySelector('[data-teeinblue-pdp] #teeAtcButton');
    if (!button) return;
    clearTimeout(errorTimer);
    pending = true;
    confirmed = nativeSuccessHandled = announceAddition = false;
    button.removeAttribute('data-fizzy-cart-success');
    button.setAttribute('aria-busy', 'true');
    clearTimeout(timer);
    state('loading');
    emit('cart:open', { returnFocusElement: button, openingClick: event?.type === 'click' ? event : null });
    // Never strand a customer behind a spinner if an app/network callback vanishes.
    timer = setTimeout(() => { if (pending) state('error'); }, 30000);
  }
  function added() {
    if (!drawer) return;
    confirmed = announceAddition = true;
    if (!button) button = document.querySelector('[data-teeinblue-pdp] #teeAtcButton');
    state('loading');
    emit('ajaxProduct:added');
    if (isOpen()) root()?.setAttribute('data-fizzy-cart-feedback', '');
    if (button) {
      button.setAttribute('data-fizzy-cart-success', '');
      clearTimeout(checkTimer);
      checkTimer = setTimeout(() => button?.removeAttribute('data-fizzy-cart-success'), 1200);
    }
  }
  function opened() {
    document.documentElement.classList.add('fizzy-cart-open');
    overlay.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    // Impulse installs its focus trap in the same event turn.
    requestAnimationFrame(focusDrawer);
  }
  function focusDrawer() {
    if (isOpen() && !drawer.contains(document.activeElement)) {
      drawer.querySelector('.js-drawer-close').focus({ preventScroll: true });
    }
  }
  function closed() {
    announceAddition = false;
    document.documentElement.classList.remove('fizzy-cart-open');
    if (overlay) overlay.hidden = true;
    drawer?.setAttribute('aria-hidden', 'true');
    const confirmation = drawer?.querySelector('[data-fizzy-cart-confirmation]');
    if (confirmation) confirmation.hidden = true;
  }
  function rendered(event) {
    if (event.detail?.location !== 'cart-drawer' || (pending && !confirmed)) return;
    clearTimeout(timer);
    pending = false;
    button?.removeAttribute('aria-busy');
    if (!drawer.querySelector('.cart__item')) announceAddition = false;
    state('ready');
    const target = drawer.querySelector('[data-cart-shipping-position]');
    const bar = drawer.querySelector('[data-products] .fizzy-shipping-progress');
    target.replaceChildren(...(bar ? [bar] : []));
  }
  function failure() {
    if (!pending || confirmed) return;
    pending = false;
    button?.removeAttribute('aria-busy');
    clearTimeout(timer);
    state('ready');
    emit('cart:close');
    const error = [...(root()?.querySelectorAll('.tee-error') || [])].find(node =>
      node.textContent.trim() && node.getClientRects().length);
    if (!error) { button?.focus(); return; }
    // Keep the native message and validation; reveal it instead of an obscured ATC.
    // Impulse restores the opener 500 ms after close; run after that callback.
    errorTimer = setTimeout(() => {
      if (!error.isConnected || isOpen()) return;
      const pdp = error.closest('[data-teeinblue-pdp]');
      const close = pdp?.querySelector('.tee-sticky-close');
      if (pdp?.classList.contains('tee-gallery-sticky') &&
          !pdp.classList.contains('tee-gallery-hidden') && close && !close.hidden) close.click();
      const temporaryFocus = !error.hasAttribute('tabindex');
      if (temporaryFocus) {
        error.tabIndex = -1;
        error.addEventListener('blur', () => error.removeAttribute('tabindex'), { once: true });
      }
      error.focus({ preventScroll: true });
      error.scrollIntoView({ block: 'center', behavior: 'instant' });
    }, 500);
  }
  function mount() {
    const next = document.getElementById('CartDrawer');
    if (!next || next === drawer) return;
    drawer = next;
    overlay = document.querySelector('[data-fizzy-cart-overlay]');
    if (!overlay || !drawer.querySelector('[data-fizzy-cart-loading]')) { drawer = null; return; }
    drawer.querySelector('[data-fizzy-cart-continue]').addEventListener('click', () => emit('cart:close'), options);
    // Mobile WebKit can reject focusing a control while it is still off-screen.
    drawer.addEventListener('transitionend', event => { if (event.target === drawer) focusDrawer(); }, options);
    overlay.addEventListener('click', () => emit('cart:close'), options);
    state('ready');
  }
  // Show pending feedback before native validation/customisation work begins.
  // Let the original click and all native required-field checks run unchanged.
  document.addEventListener('click', event => {
    const target = event.target.closest?.('[data-teeinblue-pdp] #teeAtcButton');
    if (target && !target.disabled) begin(event);
  }, { ...options, capture: true });
  document.addEventListener('teeinblue-event-before-customization-created', begin, options);
  document.addEventListener('teeinblue-event-before-cart-added', begin, options);
  document.addEventListener('teeinblue-event-after-cart-added', () => { nativeSuccessHandled = true; added(); }, options);
  document.addEventListener('drawerOpen.CartDrawer', opened, options);
  document.addEventListener('drawerClose.CartDrawer', closed, options);
  document.addEventListener('cart:rendered', rendered, options);
  document.addEventListener('cart:render-error', event => {
    if (pending && !confirmed) return;
    if (event.detail?.location === 'cart-drawer') { clearTimeout(timer); pending = false; state('error'); }
  }, options);
  document.addEventListener('fizzy:cart:failure', failure, options);
  document.addEventListener('shopify:section:load', mount, options);
  document.addEventListener('shopify:section:unload', event => {
    if (drawer && event.target.contains(drawer)) { closed(); clearTimeout(timer); drawer = null; pending = false; }
  }, options);
  window.FizzyCartDrawer = {
    get nativeSuccessHandled() { return nativeSuccessHandled; },
    addedFallback: added,
    destroy() { closed(); lifecycle.abort(); clearTimeout(timer); clearTimeout(checkTimer); clearTimeout(errorTimer); delete window.FizzyCartDrawer; }
  };
  mount();
})();
